import { createAsyncThunk } from '@reduxjs/toolkit';
import type { ProcessoArbitralStatus } from '@/features/arbitral';
import {
  type RecusaDoUsoDocumentoPadrao,
  recusaDoUsoDocumentoPadrao,
} from '@/features/documentosPadrao';
import { getErrorMessage } from '@/shared/utils/error';
import type { RootState } from '@/store';
import type {
  AndamentoProcesso,
  CancelarProcessoResultado,
  CreateParteProcessoPayload,
  CriarSolicitacaoPayload,
  DocumentoProcesso,
  MensagemChatItem,
  ProcessoArbitralDetail,
  SituacaoAtiva,
  SolicitacaoAlteracao,
} from '../types/arbitralDetailTypes';
import type {
  DocumentoPadraoJuntado,
  JuntarDocumentoPadraoPayload,
} from '../types/juntadaDocumentoPadraoTypes';
import { arbitralDetalhesService, type PatchParteBody } from './services';

/** As frases que só os autos têm; as da biblioteca e dos status vêm de `recusaDoUsoDocumentoPadrao`. */
const MENSAGENS_DA_JUNTADA_NOS_AUTOS: Record<string, string> = {
  PROCESSO_ENCERRADO: 'Este processo foi encerrado e não aceita mais documentos.',
  DOCUMENTO_PADRAO_JA_JUNTADO: 'Esta versão do documento já está nos autos deste processo.',
};

export const fetchProcessoArbitral = createAsyncThunk<
  ProcessoArbitralDetail,
  string,
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/fetchProcesso', async (processoId, { rejectWithValue }) => {
  try {
    return await arbitralDetalhesService.fetchProcesso(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const patchParteContato = createAsyncThunk<
  void,
  { processoId: string; parteId: string; data: PatchParteBody },
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/patchParte', async ({ processoId, parteId, data }, { rejectWithValue }) => {
  try {
    await arbitralDetalhesService.patchParte(processoId, parteId, data);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const createParteArbitral = createAsyncThunk<
  ProcessoArbitralDetail,
  { processoId: string; data: CreateParteProcessoPayload },
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/createParte', async ({ processoId, data }, { rejectWithValue }) => {
  try {
    await arbitralDetalhesService.createParte(processoId, data);
    return await arbitralDetalhesService.fetchProcesso(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const reenviarNotificacaoBounce = createAsyncThunk<
  void,
  { processoId: string; parteId: string },
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/reenviarNotificacao', async ({ processoId, parteId }, { rejectWithValue }) => {
  try {
    await arbitralDetalhesService.reenviarNotificacao(processoId, parteId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const reabrirProcessoArbitral = createAsyncThunk<
  { novoStatus: ProcessoArbitralStatus },
  { processoId: string; novoStatus: ProcessoArbitralStatus; motivo: string },
  { rejectValue: string; state: RootState }
>(
  'arbitralDetalhes/reabrirProcesso',
  async ({ processoId, novoStatus, motivo }, { rejectWithValue }) => {
    try {
      await arbitralDetalhesService.reabrirProcesso(processoId, { novoStatus, motivo });
      return { novoStatus };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const resolverPendencia = createAsyncThunk<
  { pendenciaId: string },
  { processoId: string; pendenciaId: string; resposta: string; arquivos?: File[] },
  { rejectValue: string; state: RootState }
>(
  'arbitralDetalhes/resolverPendencia',
  async ({ processoId, pendenciaId, resposta, arquivos }, { rejectWithValue }) => {
    try {
      await arbitralDetalhesService.resolverPendencia(processoId, pendenciaId, resposta, arquivos);
      return { pendenciaId };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const reenviarPendencia = createAsyncThunk<
  { pendenciaId: string },
  { processoId: string; pendenciaId: string; resposta: string; arquivos?: File[] },
  { rejectValue: string; state: RootState }
>(
  'arbitralDetalhes/reenviarPendencia',
  async ({ processoId, pendenciaId, resposta, arquivos }, { rejectWithValue }) => {
    try {
      await arbitralDetalhesService.reenviarPendencia(processoId, pendenciaId, resposta, arquivos);
      return { pendenciaId };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchSituacoesDisponiveis = createAsyncThunk<
  SituacaoAtiva[],
  void,
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/fetchSituacoesDisponiveis', async (_, { rejectWithValue }) => {
  try {
    return await arbitralDetalhesService.listarSituacoesDisponiveis();
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const vincularSituacaoArbitral = createAsyncThunk<
  SituacaoAtiva[],
  { processoId: string; situacaoId: string },
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/vincularSituacao', async ({ processoId, situacaoId }, { rejectWithValue }) => {
  try {
    return await arbitralDetalhesService.vincularSituacoes(processoId, [situacaoId]);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const desvincularSituacaoArbitral = createAsyncThunk<
  SituacaoAtiva[],
  { processoId: string; situacaoId: string },
  { rejectValue: string; state: RootState }
>(
  'arbitralDetalhes/desvincularSituacao',
  async ({ processoId, situacaoId }, { rejectWithValue }) => {
    try {
      return await arbitralDetalhesService.desvincularSituacao(processoId, situacaoId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchDocumentosArbitral = createAsyncThunk<
  DocumentoProcesso[],
  string,
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/fetchDocumentos', async (processoId, { rejectWithValue }) => {
  try {
    return await arbitralDetalhesService.listarDocumentos(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const removerDocumentoArbitral = createAsyncThunk<
  { docId: string },
  { processoId: string; docId: string },
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/removerDocumento', async ({ processoId, docId }, { rejectWithValue }) => {
  try {
    await arbitralDetalhesService.removerDocumento(processoId, docId);
    return { docId };
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const fetchAndamentosArbitral = createAsyncThunk<
  AndamentoProcesso[],
  string,
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/fetchAndamentos', async (processoId, { rejectWithValue }) => {
  try {
    return await arbitralDetalhesService.listarAndamentos(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const criarAndamentoArbitral = createAsyncThunk<
  AndamentoProcesso,
  {
    processoId: string;
    descricao: string;
    arquivos: File[];
  },
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/criarAndamento', async ({ processoId, ...dados }, { rejectWithValue }) => {
  try {
    return await arbitralDetalhesService.criarAndamento(processoId, dados);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Juntada de documento padrão pela aba Documentos. Resolve só com a juntada persistida e as duas
 * abas já recarregadas: o painel fecha no `fulfilled`, e fechar antes deixaria a pessoa olhando a
 * lista velha. Falha ao recarregar não desfaz nem esconde a juntada — cada recarga já trata o
 * próprio erro.
 */
export const juntarDocumentoPadraoArbitral = createAsyncThunk<
  DocumentoPadraoJuntado,
  { processoId: string } & JuntarDocumentoPadraoPayload,
  { rejectValue: RecusaDoUsoDocumentoPadrao; state: RootState }
>(
  'arbitralDetalhes/juntarDocumentoPadrao',
  async ({ processoId, ...payload }, { dispatch, rejectWithValue }) => {
    let juntado: DocumentoPadraoJuntado;
    try {
      juntado = await arbitralDetalhesService.juntarDocumentoPadrao(processoId, payload);
    } catch (erro) {
      return rejectWithValue(recusaDoUsoDocumentoPadrao(erro, MENSAGENS_DA_JUNTADA_NOS_AUTOS));
    }
    await Promise.all([
      dispatch(fetchDocumentosArbitral(processoId)),
      dispatch(fetchAndamentosArbitral(processoId)),
    ]);
    return juntado;
  },
);

export const downloadDocumentoArbitral = createAsyncThunk<
  void,
  { processoId: string; docId: string; nomeOriginal: string },
  { rejectValue: string; state: RootState }
>(
  'arbitralDetalhes/downloadDocumento',
  async ({ processoId, docId, nomeOriginal }, { rejectWithValue }) => {
    try {
      await arbitralDetalhesService.baixarDocumento(processoId, docId, nomeOriginal);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchMensagensChat = createAsyncThunk<
  { mensagens: MensagemChatItem[]; total: number },
  string,
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/fetchMensagensChat', async (processoId, { rejectWithValue }) => {
  try {
    return await arbitralDetalhesService.listarMensagensChat(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const enviarMensagemChat = createAsyncThunk<
  MensagemChatItem,
  { processoId: string; texto: string; arquivos: File[] },
  { rejectValue: string; state: RootState }
>(
  'arbitralDetalhes/enviarMensagemChat',
  async ({ processoId, texto, arquivos }, { rejectWithValue }) => {
    try {
      return await arbitralDetalhesService.enviarMensagemChat(processoId, texto, arquivos);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const marcarMensagemLida = createAsyncThunk<
  void,
  { processoId: string; msgId: string },
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/marcarMensagemLida', async ({ processoId, msgId }, { rejectWithValue }) => {
  try {
    await arbitralDetalhesService.marcarMensagemLida(processoId, msgId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const fetchNaoLidasChat = createAsyncThunk<
  number,
  string,
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/fetchNaoLidasChat', async (processoId, { rejectWithValue }) => {
  try {
    return await arbitralDetalhesService.contarNaoLidasChat(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const fetchSolicitacoesArbitral = createAsyncThunk<
  SolicitacaoAlteracao[],
  string,
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/fetchSolicitacoes', async (processoId, { rejectWithValue }) => {
  try {
    return await arbitralDetalhesService.listarSolicitacoes(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const criarSolicitacaoArbitral = createAsyncThunk<
  SolicitacaoAlteracao,
  { processoId: string; payload: CriarSolicitacaoPayload },
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/criarSolicitacao', async ({ processoId, payload }, { rejectWithValue }) => {
  try {
    return await arbitralDetalhesService.criarSolicitacao(processoId, payload);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const cancelarSolicitacaoArbitral = createAsyncThunk<
  SolicitacaoAlteracao,
  { processoId: string; solicitacaoId: string },
  { rejectValue: string; state: RootState }
>(
  'arbitralDetalhes/cancelarSolicitacao',
  async ({ processoId, solicitacaoId }, { rejectWithValue }) => {
    try {
      return await arbitralDetalhesService.cancelarSolicitacao(processoId, solicitacaoId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const cancelarProcessoArbitral = createAsyncThunk<
  { resultado: CancelarProcessoResultado; processo: ProcessoArbitralDetail },
  { processoId: string },
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/cancelarProcesso', async ({ processoId }, { rejectWithValue }) => {
  try {
    const resultado = await arbitralDetalhesService.cancelarProcesso(processoId);
    const processo = await arbitralDetalhesService.fetchProcesso(processoId);
    return { resultado, processo };
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Reenvia a 2ª via do link de pagamento para o e-mail do usuário autenticado.
 *
 * Thunk, e não chamada direta ao service: componente `use client` não importa
 * service (regra `local/no-services-in-use-client`). O corpo vai vazio de
 * propósito — o destinatário é resolvido no servidor pelo token, e mandar um
 * endereço daqui devolve 400.
 */
export const enviarSegundaViaCobranca = createAsyncThunk<
  void,
  { processoId: string; cobrancaId: string },
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/enviarSegundaVia', async ({ processoId, cobrancaId }, { rejectWithValue }) => {
  try {
    await arbitralDetalhesService.enviarSegundaVia(processoId, cobrancaId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * URL do comprovante da transação. Devolve a URL para o componente abrir — o
 * destino é do gateway, então quem abre passa pelo guard de host antes.
 */
export const obterReciboCobranca = createAsyncThunk<
  string,
  { processoId: string; cobrancaId: string },
  { rejectValue: string; state: RootState }
>('arbitralDetalhes/obterRecibo', async ({ processoId, cobrancaId }, { rejectWithValue }) => {
  try {
    return await arbitralDetalhesService.obterRecibo(processoId, cobrancaId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Pede nova cobrança para a custa em atraso.
 *
 * O servidor cancela a anterior, confere que o gateway aceitou o cancelamento e
 * retoma o nó de cobrança do fluxo — é o motor que emite a nova e reaponta a
 * espera. O front não fabrica vencimento nem anuncia cobrança inexistente.
 */
export const regerarCobrancaVencida = createAsyncThunk<
  void,
  { processoId: string; cobrancaId: string; motivo: string },
  { rejectValue: string; state: RootState }
>(
  'arbitralDetalhes/regerarCobranca',
  async ({ processoId, cobrancaId, motivo }, { rejectWithValue }) => {
    try {
      await arbitralDetalhesService.regerarCobranca(processoId, cobrancaId, motivo);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);
